<template>
  <Head title="Tag" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Tag', href: route('tags.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-tags sm:mr-3"></i>
                  {{ form.id ? "Edit Tag" : "Tambah Tag" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submitForm">
                  <!-- Input Fields -->
                  <div>
                    <InputField
                      label="Nama Tag"
                      icon="tag"
                      v-model="form.name"
                      placeholder="Masukkan nama tag"
                    />
                    <InputError :message="form.errors.name" class="mt-2" />
                  </div>

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{
                        form.processing
                          ? "Menyimpan..."
                          : isEditMode
                          ? "Update Tag"
                          : "Simpan Tag"
                      }}
                    </PrimaryBtn>

                    <button
                      v-if="isEditMode"
                      type="button"
                      @click="cancelEdit"
                      class="w-full mt-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-times"></i>
                      Batal
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Tag
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola tag sistem untuk konten
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ totalItems }} tag
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Cari nama tag..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Nama Tag
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <!-- Table Rows -->
                    <tr
                      v-for="(tag, index) in paginatedTags"
                      :key="tag.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ getRowNumber(index) }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <span class="mr-2 text-indigo-600">🏷️</span>
                          <span class="font-medium text-gray-900">
                            {{ tag.name }}
                          </span>
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons (Edit, Delete) -->
                          <button
                            @click="editTag(tag)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <i class="w-3 h-3 fa-solid fa-pen"></i>
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <button
                            @click="deleteTag(tag.id)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <i class="w-3 h-3 fa-solid fa-trash"></i>
                            <span class="hidden ml-1 sm:inline">Delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <Pagination
              :data="paginationData"
              :updatedPageNumber="updatedPageNumber"
            />
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch, inject } from "vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import Pagination from "@/Components/Pagination.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Swal from "sweetalert2";
const route = inject('route');

const props = defineProps({
  tags: {
    type: Array,
    required: true,
  },
});

let search = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);

const filteredTags = computed(() => {
  if (!search.value) return props.tags;
  return props.tags.filter((tag) =>
    tag.name.toLowerCase().includes(search.value.toLowerCase())
  );
});

// Pagination computed properties
const totalItems = computed(() => filteredTags.value.length);
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const paginatedTags = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredTags.value.slice(start, end);
});

const startIndex = computed(() => {
  if (totalItems.value === 0) return 0;
  return (currentPage.value - 1) * itemsPerPage.value + 1;
});

const endIndex = computed(() => {
  const end = currentPage.value * itemsPerPage.value;
  return end > totalItems.value ? totalItems.value : end;
});

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, currentPage.value + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

// Row number calculation with fallback
const getRowNumber = (index) => {
  const calculated = (currentPage.value - 1) * itemsPerPage.value + index + 1;
  return isNaN(calculated) ? index + 1 : calculated;
};

// Create pagination data object compatible with Pagination component
const paginationData = computed(() => ({
  current_page: currentPage.value,
  last_page: totalPages.value,
  total: totalItems.value,
  per_page: itemsPerPage.value,
  from: startIndex.value,
  to: endIndex.value,
  data: paginatedTags.value,
}));

// Handle page updates from Pagination component
const updatedPageNumber = (page) => {
  currentPage.value = page;
};

// Pagination methods
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Reset to page 1 when search changes
watch(search, () => {
  currentPage.value = 1;
});

const form = useForm({
  id: null,
  name: "",
});

const submitForm = () => {
  if (form.id) {
    form.put(route("tags.update", form.id), {
      onSuccess: () => {
        Swal.fire({
          title: "Berhasil!",
          text: "Tag berhasil diperbarui",
          icon: "success",
          confirmButtonText: "OK",
        });
        form.reset();
        form.id = null;
        isEditMode.value = false;
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Gagal memperbarui tag",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  } else {
    form.post(route("tags.store"), {
      onSuccess: () => {
        Swal.fire({
          title: "Berhasil!",
          text: "Tag berhasil disimpan",
          icon: "success",
          confirmButtonText: "OK",
        });
        form.reset();
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Gagal menyimpan tag",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  }
};

const isEditMode = ref(false);

const editTag = (tag) => {
  isEditMode.value = true;
  form.id = tag.id;
  form.name = tag.name;
};

const cancelEdit = () => {
  isEditMode.value = false;
  form.reset();
  form.id = null;
};

const deleteTag = async (id) => {
  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: "Tag akan dihapus permanen!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus tag",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    router.delete(route("tags.destroy", id), {
      onSuccess: () => {
        Swal.fire({
          title: "Terhapus!",
          text: "Tag berhasil dihapus",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Error!",
          text: "Terjadi kesalahan saat menghapus tag",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
      preserveScroll: true,
    });
  }
};
</script>
